import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ProjectRequestProvider } from './contexts/ProjectRequestContext';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ForcePasswordGate from './components/ForcePasswordGate';
import ForceEmailGate from './components/ForceEmailGate';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import Studio from './pages/Studio';
import Legal from './pages/Legal';
import Auth from './pages/Auth';
import Account from './pages/Account';
import Admin from './pages/Admin';
import Finance from './pages/Finance';
import UserProfile from './pages/UserProfile';
import Feed from './pages/Feed';
import Explore from './pages/Explore';
import ComingSoon from './pages/ComingSoon';
import DevHub from './pages/DevHub';
import { settingsService } from './services/apiService';
import { shouldShowComingSoon } from './utils/siteAccess';

const EMPTY_SETTINGS = {
  maintenanceMode: false,
  devHubPublicAccess: false,
};

function AppRoutes() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [siteSettings, setSiteSettings] = useState(EMPTY_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      try {
        const response = await settingsService.getPublic();
        if (active) {
          setSiteSettings({
            maintenanceMode: Boolean(response?.data?.settings?.maintenanceMode),
            devHubPublicAccess: Boolean(response?.data?.settings?.devHubPublicAccess),
          });
        }
      } catch {
        if (active) setSiteSettings(EMPTY_SETTINGS);
      } finally {
        if (active) setSettingsLoading(false);
      }
    };
    loadSettings();
    const intervalId = window.setInterval(loadSettings, 60_000);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const showComingSoon = shouldShowComingSoon({
    maintenanceMode: siteSettings.maintenanceMode,
    devHubPublicAccess: siteSettings.devHubPublicAccess,
    pathname: location.pathname,
    isAdmin: user?.role === 'admin',
  });

  if (!authLoading && !settingsLoading && showComingSoon) {
    return (
      <ComingSoon
        allowDevHub={siteSettings.devHubPublicAccess}
        mode={siteSettings.devHubPublicAccess && !siteSettings.maintenanceMode ? 'devhub' : 'maintenance'}
      />
    );
  }

  return (
    <ForcePasswordGate>
      <ForceEmailGate>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/studio" element={<Studio />} />
          <Route path="/dev-hub" element={<DevHub />} />
          <Route path="/devhub" element={<DevHub />} />
          <Route path="/termos" element={<Legal />} />
          <Route path="/entrar" element={<Auth />} />
          <Route path="/login" element={<Auth />} />
          <Route path="/perfil/:username" element={<UserProfile />} />
          <Route
            path="/conta"
            element={(
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/feed"
            element={(
              <ProtectedRoute>
                <Feed />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/explorar"
            element={(
              <ProtectedRoute>
                <Explore />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/admin"
            element={(
              <ProtectedRoute roles={['admin']}>
                <Admin />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/financas"
            element={(
              <ProtectedRoute requireFinance>
                <Finance />
              </ProtectedRoute>
            )}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ForceEmailGate>
    </ForcePasswordGate>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider>
          <Router>
            <ProjectRequestProvider>
              <AppRoutes />
            </ProjectRequestProvider>
          </Router>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
